import { useCallback, useEffect, useMemo, useState } from 'react';
import { EPaymentsMode } from '~/processes/providers/authorization';
import { useStore } from '~/shared/hooks/useStore';
import classes from '../payment.module.scss';
import { useTranslation } from 'react-i18next';
import { formatNumberPrice } from '~/shared/lib/helpers';

enum ELimitValue {
    MAX = 'max',
    MIN = 'min',
}

enum EValueType {
    ABSOLUTE = 'absolute',
    PERCENTAGE = 'percentage',
}

/** Используется для управления суммой платежа.
 * Добавляет возможность управления суммой частичного платежа
 * */
export const usePaymentSum = () => {
    const { COrder, CSettings } = useStore();
    const { need_to_pay } = COrder.order.order_info;
    const { payments_sum } = COrder.order.order_payments;
    const [paymentSum, setPaymentSum] = useState(need_to_pay);
    const { partial_payment } = CSettings.settings;
    const { t } = useTranslation('components');

    // Синхронизация paymentSum с need_to_pay
    useEffect(() => {
        setPaymentSum(need_to_pay);
    }, [need_to_pay]);

    const isFirstPayment = useMemo(() => {
        const sumArray = Object.values(payments_sum);
        return !sumArray.reduce((a, b) => a + b, 0);
    }, [payments_sum]);

    const getAbsoluteOrPercentage = useCallback((value: number): EValueType => {
        if (Number.isInteger(value)) {
            return EValueType.ABSOLUTE;
        }

        return EValueType.PERCENTAGE;
    }, []);

    const calculateLimitValue = useCallback((type: ELimitValue) => {
        if (!partial_payment || !isFirstPayment) {
            return need_to_pay;
        }

        const valueType = getAbsoluteOrPercentage(partial_payment.value);
        const isEqualOrLess = partial_payment.type === EPaymentsMode.EQUAL || partial_payment.type === EPaymentsMode.LESS;
        const isEqualOrMore = partial_payment.type === EPaymentsMode.EQUAL || partial_payment.type === EPaymentsMode.MORE;

        switch (valueType) {
        case EValueType.ABSOLUTE: {
            if (type === ELimitValue.MAX) {
                return isEqualOrLess ? partial_payment.value : need_to_pay;
            }

            if (type === ELimitValue.MIN) {
                return isEqualOrMore ? partial_payment.value : 0;
            }

            return need_to_pay;
        }

        case EValueType.PERCENTAGE: {
            if (type === ELimitValue.MAX) {
                return isEqualOrLess ? partial_payment.value * need_to_pay : need_to_pay;
            }

            if (type === ELimitValue.MIN) {
                return isEqualOrMore ? partial_payment.value * need_to_pay : 0;
            }

            return need_to_pay;
        }}
    }, [need_to_pay, partial_payment, isFirstPayment]);

    const maxSum = useMemo(() => calculateLimitValue(ELimitValue.MAX), [calculateLimitValue]);
    const minSum = useMemo(() => calculateLimitValue(ELimitValue.MIN), [calculateLimitValue]);

    const showInput = useMemo(() => {
        if (partial_payment && partial_payment.type !== EPaymentsMode.EQUAL && isFirstPayment) {
            return true;
        }

        return false;
    }, [partial_payment, isFirstPayment]);

    const getDefaultSum = useCallback(() => {
        if (!partial_payment || !isFirstPayment) {
            return need_to_pay;
        }

        const isEqualOrLess = partial_payment.type === EPaymentsMode.EQUAL || partial_payment.type === EPaymentsMode.LESS;
        const isMore = partial_payment.type === EPaymentsMode.MORE;
        const valueType = getAbsoluteOrPercentage(partial_payment.value);

        if (isEqualOrLess && valueType === EValueType.ABSOLUTE) {
            return partial_payment.value;
        }
        if (isEqualOrLess && valueType === EValueType.PERCENTAGE) {
            return partial_payment.value * need_to_pay;
        }
        if (isMore) {
            return need_to_pay;
        }
        return need_to_pay;
    }, [need_to_pay, partial_payment, isFirstPayment]);

    // Обновление paymentSum при изменении getDefaultSum только если partial_payment включен
    useEffect(() => {
        if (partial_payment && isFirstPayment) {
            setPaymentSum(getDefaultSum());
        } else {
            setPaymentSum(need_to_pay);
        }
    }, [getDefaultSum, need_to_pay, partial_payment, isFirstPayment]);

    const setCorrectSum = useCallback((sum: number, isOnBlur = false) => {
        if (sum > maxSum) {
            setPaymentSum(maxSum);
            return;
        }
        if (sum < minSum) {
            setPaymentSum(minSum);
            return;
        }
        if (!isOnBlur) {
            setPaymentSum(sum);
        }
    }, [maxSum, minSum]);

    const onChangeSum = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const value = Number(e.target.value);
        setCorrectSum(value);
    }, [setCorrectSum]);

    const onBlur = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
        const value = Number(e.target.value);
        setCorrectSum(value, true);
    }, [setCorrectSum]);

    const renderTotalSumBlock = (orderDebt: number) => (
        <div className={classes.total}>
            { isFirstPayment && partial_payment && partial_payment.value < 1 && partial_payment.type === EPaymentsMode.EQUAL ? (
                <>
                    <span>
                        { t('payments.information.partialPayment', { percent: partial_payment.value * 100 })}
                    </span>
                    <span className={classes.key}>
                        {formatNumberPrice(paymentSum)}
                    </span>
                </>
            ) : (
                <>
                    <span>
                        {t('payments.information.totalPayment')}
                    </span>
                    <span className={classes.key}>
                        {formatNumberPrice(orderDebt)}
                    </span>
                </>
            )  
            }
        </div>
    );

    return {
        paymentSum,
        partial_payment,
        maxSum,
        minSum,
        showInput,
        onChangeSum,
        onBlur,
        renderTotalSumBlock,
    };
};